/**
 * User info component showing current user and logout option.
 */
import React, { useState } from 'react';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  Chip
} from '@mui/material';
import {
  AccountCircle,
  Logout as LogoutIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useAuthConfig } from '../contexts/AuthContext';

const UserInfo = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAuthenticated, getUserRole } = useAuth();
  const { isConfigured } = useAuthConfig();
  const userRole = getUserRole();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  // Jeśli uwierzytelnianie nie jest skonfigurowane lub użytkownik nie jest zalogowany
  if (!isConfigured || !isAuthenticated || !user) {
    return null;
  }

  // Pobierz inicjały użytkownika
  const getInitials = (name) => {
    if (!name) return '?';
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        color="inherit"
        sx={{
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'secondary.main',
            fontSize: '0.875rem',
            fontWeight: 600
          }}
        >
          {getInitials(user.name)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {user.name || user.username}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {user.email}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={userRole}
              size="small"
              color={userRole === 'Administrator' ? 'error' : userRole === 'Serwisant' ? 'warning' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<SecurityIcon />}
              label="Microsoft Entra ID"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Wyloguj" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserInfo;